import { Injectable, inject } from '@angular/core';
import { HttpRequest } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { ENVIRONMENT_CONFIG } from '../config/environment.config';
import { MOCK_ROUTES, MockRoute } from './mock-config';
import { DictionaryMockState } from './mock-dictionary-state';
import { resolveUserDisplayName, resolveUserInitials } from '@victory-lib-ui/shared/models';

interface DemoUser {
  id?: number;
  user_id: string;
  name: string;
  nickname?: string | null;
  email: string;
  password: string;
  role: string;
  display_name?: string | null;
  phone_number?: string | null;
  bio?: string | null;
  status?: string | null;
  initials?: string | null;
  avatar_url?: string | null;
  avatar_fallback_url?: string | null;
  profile_image_url?: string | null;
  profile_image?: {
    url: string | null;
    storage: string | null;
    path: string | null;
    file_name: string | null;
    mime_type: string | null;
    size_bytes: number | null;
    uploaded_at: string | null;
  } | null;
  settings?: {
    preferences?: Record<string, unknown>;
  } | null;
  created_at?: string;
  updated_at?: string;
}

interface DemoSettingsPayload {
  user: DemoUser;
  settings: {
    preferences?: Record<string, unknown>;
  };
}
interface DemoHighlight {
  id: string;
  verse_id: string;
  chapter_usfm: string;
  color: string;
  version: string;
  user_id: string;
}
interface DemoNote {
  id: string;
  verse_id: string;
  chapter_usfm: string;
  type: 'text' | 'audio';
  content: string | null;
  audio_url: string | null;
  duration: string | null;
  version: string;
  user_id: string;
}
interface ReadingSession {
  id: string;
  user_id: string;
  book: string;
  chapter: string;
  version: string;
  duration: number;
  status: 'active' | 'ended';
}

interface DemoAudioVersion {
  version_id: string;
  label: string;
  name: string;
  language?: string | null;
  audio_count: number;
}

interface DemoAudioBook {
  code: string;
  name: string;
  abbreviation: string;
  testament: string;
  chapter_count: number;
  available_audio_chapters: number;
  has_audio: boolean;
  sort_order: number;
}

interface DemoAudioBibleBookChapter {
  chapter: number;
  label: string;
  usfm: string;
}

interface DemoAudioBibleBook {
  code: string;
  name: string;
  abbreviation: string;
  testament: string;
  chapter_count: number;
  sort_order: number;
  chapters: DemoAudioBibleBookChapter[];
}

interface DemoAudioChapterSummary {
  chapter: number;
  has_audio: boolean;
  speaker_count: number;
  duration_seconds: number;
  label: string;
}

interface DemoAudioVerse {
  verse_number: number;
  text: string;
}

interface DemoAudioReference {
  version_id: string;
  version_label: string;
  version_title: string;
  book_id: string;
  book_name: string;
  chapter: number;
  chapter_usfm: string;
  title: string;
  subtitle?: string | null;
  previous_ref?: string | null;
  next_ref?: string | null;
  copyright?: string | null;
}

interface DemoAudioSpeakerProfile {
  name: string;
  details: string;
  voice_type: string;
  is_premium?: boolean;
  source?: string | null;
  language?: string | null;
  gender?: string | null;
  country?: string | null;
}

interface DemoAudioSpeakerSeed {
  speaker_id: number;
  audio_url: string;
  chunks_url?: string;
  chunk_durations?: number[];
}

interface DemoAudioPageSeed {
  reference: DemoAudioReference;
  verses: DemoAudioVerse[];
  speakers: DemoAudioSpeakerSeed[];
}

interface DemoAudioSeed {
  versions: DemoAudioVersion[];
  booksByVersion: Record<string, DemoAudioBook[]>;
  bibleBooksByVersion: Record<string, DemoAudioBibleBook[]>;
  chaptersByVersionBook: Record<
    string,
    Record<string, DemoAudioChapterSummary[]>
  >;
  speakerLibrary: Record<string, DemoAudioSpeakerProfile>;
  pages: Record<string, DemoAudioPageSeed>;
}

export interface MockResult {
  status?: number;
  body: unknown;
}

const DEMO_AUDIO_CHUNK_PAUSE_SECONDS = 0.25;

interface DemoAudioUserPreferences {
  audioPage?: Record<string, unknown>;
  passage?: Record<string, unknown>;
}

@Injectable({ providedIn: 'root' })
export class MockService {
  private readonly environment = inject(ENVIRONMENT_CONFIG);
  private readonly cache = new Map<string, unknown>();

  private users: DemoUser[] = [];
  private usersInitialized = false;
  private highlights: DemoHighlight[] = [];
  private notes: DemoNote[] = [];
  private readingSessions: ReadingSession[] = [];
  private readonly audioPlaybackProgress = new Map<
    string,
    {
      progress_seconds: number;
      duration_seconds: number;
      completed: boolean;
      updated_at: string;
    }
  >();
  private readonly audioUserPreferences = new Map<
    string,
    DemoAudioUserPreferences
  >();
  private readonly tokenToUserId = new Map<string, string>();
  private readonly demoSettingsByUserId = new Map<string, DemoSettingsPayload>();
  private readonly dictionaryState = new DictionaryMockState((path) =>
    this.loadJson(path)
  );
  private readonly handlerMap: Record<
    string,
    (
      req: HttpRequest<unknown>,
      path: string
    ) => Promise<MockResult | null> | MockResult | null
  > = {
    handleLogin: (req) => this.handleLogin(req),
    handleRegister: (req) => this.handleRegister(req),
    handleLogout: (req) => this.handleLogout(req),
    handleRefresh: (req) => this.handleRefresh(req),
    handleCurrentUser: (req) => this.handleCurrentUser(req),
    handleGetSettings: (req) => this.handleGetSettings(req),
    handleUpdateProfile: (req) => this.handleUpdateProfile(req),
    handleUploadProfileAvatar: (req) => this.handleUploadProfileAvatar(req),
    handleVersions: () => this.handleVersions(),
    handlePassage: (req) => this.handlePassage(req),
    handleBibleBooks: (req) => this.handleBibleBooks(req),
    handleAudioVersions: () => this.handleAudioVersions(),
    handleAudioBooks: (req) => this.handleAudioBooks(req),
    handleAudioChapters: (req) => this.handleAudioChapters(req),
    handleAudioChapter: (req) => this.handleAudioChapter(req),
    handleAudioPreferences: (req) => this.handleAudioPreferences(req),
    handleSaveAudioPreferences: (req) => this.handleSaveAudioPreferences(req),
    handleAudioTrackPlayback: (req) => this.handleAudioTrackPlayback(req),
    handlePassageThemes: (req) => this.handlePassageThemes(req),
    handleThemeChapters: (req, path) => this.handleThemeChapters(req, path),
    handleSaveHighlight: (req) => this.handleSaveHighlight(req),
    handleSaveNote: (req) => this.handleSaveNote(req),
    handleDeleteNote: (req, path) => this.handleDeleteNote(req, path),
    handleStartReadingSession: (req) => this.handleStartReadingSession(req),
    handleUpdateReadingSession: (req, path) =>
      this.handleUpdateReadingSession(req, path),
    handleEndReadingSession: (req, path) =>
      this.handleEndReadingSession(req, path),
    handleDictionaryHome: (req) => this.handleDictionaryHome(req),
    handleDictionaryEntry: (req) => this.handleDictionaryEntry(req),
    handleDictionaryResume: (req) => this.handleDictionaryResume(req),
    handleStartStudyTracking: (req) => this.handleStartStudyTracking(req),
    handleUpdateStudyTracking: (req, path) =>
      this.handleUpdateStudyTracking(req, path),
    handleEndStudyTracking: (req, path) =>
      this.handleEndStudyTracking(req, path),
  };

  resolve(req: HttpRequest<unknown>): Observable<MockResult | null> {
    if (!this.isDemoMode()) return of(null);
    return from(this.resolveAsync(req));
  }

  private async resolveAsync(
    req: HttpRequest<unknown>
  ): Promise<MockResult | null> {
    if (this.shouldSimulateError()) {
      const status = this.environment.demoConfig?.errorStatusCodes?.[0] ?? 500;
      return {
        status,
        body: {
          success: false,
          message: 'Simulated mock server error',
          data: null,
        },
      };
    }

    const requestUrl = this.parseUrl(req.url);
    const path = requestUrl.pathname.replace(/^\/api\/v1/, '');
    const method = req.method.toUpperCase();

    // Find matching route from config
    const route = this.findMatchingRoute(method, path);
    if (!route) return null;

    // Handle based on route type
    return this.handleRouteByType(route, req, path);
  }

  /**
   * Find matching route from configuration
   */
  private findMatchingRoute(method: string, path: string): MockRoute | null {
    return (
      MOCK_ROUTES.find((route) => {
        const methodMatches = route.method === '*' || route.method === method;
        if (!methodMatches) return false;

        if (typeof route.path === 'string') {
          return route.path === path;
        } else {
          return route.path.test(path);
        }
      }) ?? null
    );
  }

  /**
   * Handle route based on its type
   */
  private async handleRouteByType(
    route: MockRoute,
    req: HttpRequest<unknown>,
    path: string
  ): Promise<MockResult | null> {
    switch (route.type) {
      case 'json-file': {
        const result = await this.fromFile(route.handler);
        return route.transform
          ? {
              ...result,
              body: route.transform(result.body, {
                params: new Map(),
                body: this.extractBody(req),
              }),
            }
          : result;
      }

      case 'custom':
      case 'state-dependent': {
        const handler = this.handlerMap[route.handler];
        if (handler) {
          return handler(req, path);
        }
        console.warn(`Mock handler not found: ${route.handler}`);
        return null;
      }

      default:
        console.warn(`Unknown mock route type: ${route.type}`);
        return null;
    }
  }

  //--------------------------------------------------------------------------
  // Utility Methods
  //--------------------------------------------------------------------------

  private extractBody(req: HttpRequest<unknown>): Record<string, unknown> {
    if (req.body instanceof FormData) {
      const entries: Record<string, unknown> = {};
      req.body.forEach((value, key) => {
        entries[key] = value;
      });
      return entries;
    }

    return (req.body as Record<string, unknown>) ?? {};
  }

  private parseUrl(rawUrl: string): URL {
    return new URL(rawUrl, 'http://mock.local');
  }

  private getParam(req: HttpRequest<unknown>, key: string): string {
    return (
      req.params.get(key) ?? this.parseUrl(req.url).searchParams.get(key) ?? ''
    );
  }

  private getCurrentUserId(req: HttpRequest<unknown>): string {
    const auth = req.headers.get('Authorization') || '';
    const token = auth.startsWith('Bearer ') ? auth.slice(7) : '';
    if (token && this.tokenToUserId.has(token))
      return this.tokenToUserId.get(token) ?? 'demo-user-001';
    return 'demo-user-001';
  }

  private issueToken(userId: string): {
    accessToken: string;
    refreshToken: string;
  } {
    const accessToken = `demo-token-${userId}-${Date.now()}`;
    const refreshToken = `demo-refresh-${userId}-${Date.now()}`;
    this.tokenToUserId.set(accessToken, userId);
    return { accessToken, refreshToken };
  }

  private shouldSimulateError(): boolean {
    const rate = this.environment.demoConfig?.errorRate ?? 0;
    return rate > 0 && Math.random() < rate;
  }

  private isDemoMode(): boolean {
    return (
      this.environment.demo ||
      this.environment.features.enableMockData ||
      sessionStorage.getItem('victory_lib_demo_mode') === 'true'
    );
  }

  private async ensureUsersLoaded(): Promise<void> {
    if (this.usersInitialized) return;
    this.usersInitialized = true;
    try {
      const jsonUsers = await this.loadJson<DemoUser[]>(
        '/assets/mocks/auth/users.json'
      );
      for (const user of jsonUsers) {
        if (!this.users.some((u) => u.user_id === user.user_id)) {
          this.users.push(user);
        }
      }
    } catch {
      // JSON seed file not available - start with empty user list
    }

    try {
      const demoSettings = await this.loadJson<DemoSettingsPayload>(
        '/assets/mocks/settings/demo.mock.json'
      );
      this.demoSettingsByUserId.set(
        demoSettings.user.user_id,
        this.normalizeSettingsPayload(demoSettings)
      );
    } catch {
      // Settings seed file not available - defaults will be created on demand
    }
  }

  private async fromFile(path: string): Promise<MockResult> {
    return { body: await this.loadJson<Record<string, unknown>>(path) };
  }

  private async loadJson<T>(path: string): Promise<T> {
    if (this.cache.has(path)) return this.cache.get(path) as T;
    const response = await fetch(path);
    if (!response.ok) throw new Error(`Mock payload not found: ${path}`);
    const data = (await response.json()) as T;
    this.cache.set(path, data);
    return data;
  }

  private async loadJsonWithFallback<T>(
    path: string,
    fallbackPath: string
  ): Promise<T> {
    try {
      return await this.loadJson<T>(path);
    } catch {
      return this.loadJson<T>(fallbackPath);
    }
  }

  private async loadAudioSeed(): Promise<DemoAudioSeed> {
    return this.loadJson<DemoAudioSeed>('/assets/mocks/audio/seed.json');
  }

  private normalizeSettingsPayload(
    payload: DemoSettingsPayload
  ): DemoSettingsPayload {
    const user = this.createProfileFromUser(payload.user);
    return {
      user,
      settings: payload.settings ?? { preferences: {} },
    };
  }

  private createProfileFromUser(user: DemoUser): DemoUser {
    const fullName = user.name.trim() || 'Demo User';
    const nickname =
      typeof user.nickname === 'string' && user.nickname.trim() !== ''
        ? user.nickname.trim()
        : null;
    const displayName = resolveUserDisplayName({
      name: fullName,
      nickname,
      display_name: user.display_name ?? null,
    });
    const profileImageUrl = user.profile_image_url ?? user.avatar_url ?? null;
    const initials = resolveUserInitials({
      name: fullName,
      nickname,
      display_name: displayName,
      initials: user.initials ?? null,
    });
    const existingUserIndex = this.users.findIndex(
      (entry) => entry.user_id === user.user_id
    );

    return {
      id: user.id ?? (existingUserIndex >= 0 ? existingUserIndex + 1 : this.users.length + 1),
      user_id: user.user_id,
      name: fullName,
      nickname,
      display_name: displayName,
      email: user.email,
      password: user.password,
      role: user.role,
      phone_number: user.phone_number ?? null,
      bio: user.bio ?? null,
      status: user.status ?? 'active',
      initials,
      profile_image_url: profileImageUrl,
      avatar_url:
        profileImageUrl ??
        user.avatar_fallback_url ??
        '/assets/icon.png',
      avatar_fallback_url: user.avatar_fallback_url ?? '/assets/icon.png',
      profile_image:
        user.profile_image ?? {
          url: profileImageUrl,
          storage: profileImageUrl ? 'demo-mock' : null,
          path: profileImageUrl,
          file_name: profileImageUrl?.split('/').pop() ?? null,
          mime_type: profileImageUrl ? 'image/jpeg' : null,
          size_bytes: null,
          uploaded_at: user.updated_at ?? new Date().toISOString(),
        },
      settings: user.settings ?? { preferences: {} },
      created_at: user.created_at ?? new Date().toISOString(),
      updated_at: user.updated_at ?? new Date().toISOString(),
    };
  }

  private getOrCreateSettingsPayload(userId: string): DemoSettingsPayload {
    const existing = this.demoSettingsByUserId.get(userId);
    if (existing) {
      return existing;
    }

    const matchingUser = this.users.find((user) => user.user_id === userId);
    const profile = this.createProfileFromUser(
      matchingUser ?? this.createDemoLoginUser('demo@victorylib.com', 'password123')
    );
    const payload: DemoSettingsPayload = {
      user: profile,
      settings: {
        preferences: profile.settings?.preferences ?? {},
      },
    };

    this.demoSettingsByUserId.set(userId, payload);
    return payload;
  }

  private syncUserProfile(user: DemoUser): DemoUser {
    const profile = this.createProfileFromUser(user);
    const userIndex = this.users.findIndex((entry) => entry.user_id === user.user_id);
    if (userIndex >= 0) {
      this.users[userIndex] = profile;
    } else {
      this.users.push(profile);
    }

    const settings = this.getOrCreateSettingsPayload(profile.user_id);
    settings.user = profile;
    settings.settings = {
      preferences: profile.settings?.preferences ?? settings.settings.preferences ?? {},
    };

    return profile;
  }

  private normalizeChunkDurations(chunks: unknown): number[] {
    if (!Array.isArray(chunks)) {
      return [];
    }

    return chunks
      .map((chunk) => Number(chunk))
      .filter((chunk) => Number.isFinite(chunk) && chunk > 0);
  }

  private normalizePositiveInteger(
    value: unknown,
    fallback: number,
    minimum = 1
  ): number {
    const normalized = Number(value);
    if (!Number.isFinite(normalized)) {
      return fallback;
    }

    return Math.max(minimum, Math.trunc(normalized));
  }

  private normalizeNonNegativeNumber(value: unknown, fallback = 0): number {
    const normalized = Number(value);
    if (!Number.isFinite(normalized)) {
      return fallback;
    }

    return Math.max(0, normalized);
  }

  private buildMockVerseTimings(
    chunkDurations: number[],
    verses: DemoAudioVerse[]
  ): Array<{
    verse_number: number;
    start: number;
    end: number;
    chunk_index: number;
  }> {
    if (chunkDurations.length === 0 || verses.length === 0) {
      return [];
    }

    const verseCount = verses.length;
    const chunkCount = chunkDurations.length;
    const verseNumbers = verses.map((verse) => verse.verse_number);

    if (chunkCount === verseCount + 1 && chunkDurations[0]! <= 6) {
      return this.buildMockVerseTimingsFromChunkVerseNumbers(chunkDurations, [
        null,
        ...verseNumbers,
      ]);
    }

    if (chunkCount === verseCount) {
      return this.buildMockVerseTimingsFromChunkVerseNumbers(
        chunkDurations,
        verseNumbers
      );
    }

    if (chunkCount > verseCount) {
      const grouped = Array.from({ length: verseCount }, () => 0);
      chunkDurations.forEach((duration, index) => {
        const verseIndex = Math.min(
          verseCount - 1,
          Math.floor((index * verseCount) / chunkCount)
        );
        grouped[verseIndex] =
          (grouped[verseIndex] ?? 0) +
          duration +
          DEMO_AUDIO_CHUNK_PAUSE_SECONDS;
      });

      const verseTimings: Array<{
        verse_number: number;
        start: number;
        end: number;
        chunk_index: number;
      }> = [];
      let cursor = 0;
      for (let index = 0; index < verseCount; index += 1) {
        const verse = verses[index]!;
        const duration = grouped[index] ?? 0;
        verseTimings.push({
          verse_number: verse.verse_number,
          start: Number(cursor.toFixed(3)),
          end: Number((cursor + duration).toFixed(3)),
          chunk_index: index,
        });
        cursor += duration;
      }
      return verseTimings;
    }

    const totalDuration =
      this.estimateMockChunkPlaybackDuration(chunkDurations);
    const perVerseDuration = totalDuration / verseCount;
    const verseTimings: Array<{
      verse_number: number;
      start: number;
      end: number;
      chunk_index: number;
    }> = [];
    let cursor = 0;
    for (let index = 0; index < verseCount; index += 1) {
      const verse = verses[index]!;
      verseTimings.push({
        verse_number: verse.verse_number,
        start: Number(cursor.toFixed(3)),
        end: Number((cursor + perVerseDuration).toFixed(3)),
        chunk_index: Math.min(index, chunkCount - 1),
      });
      cursor += perVerseDuration;
    }

    return verseTimings;
  }

  private estimateMockChunkPlaybackDuration(chunkDurations: number[]): number {
    return (
      chunkDurations.reduce((total, duration) => total + duration, 0) +
      chunkDurations.length * DEMO_AUDIO_CHUNK_PAUSE_SECONDS
    );
  }

  private buildMockVerseTimingsFromChunkVerseNumbers(
    chunkDurations: number[],
    chunkVerseNumbers: Array<number | null>
  ): Array<{
    verse_number: number;
    start: number;
    end: number;
    chunk_index: number;
  }> {
    if (
      chunkDurations.length === 0 ||
      chunkDurations.length !== chunkVerseNumbers.length
    ) {
      return [];
    }

    const verseTimings: Array<{
      verse_number: number;
      start: number;
      end: number;
      chunk_index: number;
    }> = [];

    let cursor = 0;
    let activeTimingIndex: number | null = null;
    let activeVerseNumber: number | null = null;

    chunkDurations.forEach((duration, index) => {
      const chunkStart = cursor;
      const chunkEndWithPause =
        chunkStart + duration + DEMO_AUDIO_CHUNK_PAUSE_SECONDS;
      const verseNumber = chunkVerseNumbers[index] ?? null;

      if (typeof verseNumber === 'number' && verseNumber > 0) {
        if (
          activeTimingIndex !== null &&
          activeVerseNumber !== null &&
          activeVerseNumber !== verseNumber
        ) {
          verseTimings[activeTimingIndex] = {
            ...verseTimings[activeTimingIndex]!,
            end: Number(chunkStart.toFixed(3)),
          };
        }

        if (activeTimingIndex === null || activeVerseNumber !== verseNumber) {
          verseTimings.push({
            verse_number: verseNumber,
            start: Number(chunkStart.toFixed(3)),
            end: Number(chunkEndWithPause.toFixed(3)),
            chunk_index: index,
          });
          activeTimingIndex = verseTimings.length - 1;
          activeVerseNumber = verseNumber;
        } else {
          verseTimings[activeTimingIndex] = {
            ...verseTimings[activeTimingIndex]!,
            end: Number(chunkEndWithPause.toFixed(3)),
          };
        }
      }

      cursor = chunkEndWithPause;
    });

    if (activeTimingIndex !== null) {
      verseTimings[activeTimingIndex] = {
        ...verseTimings[activeTimingIndex]!,
        end: Number(
          Math.max(
            cursor,
            this.estimateMockChunkPlaybackDuration(chunkDurations)
          ).toFixed(3)
        ),
      };
    }

    return verseTimings;
  }

  private buildMockNarrationSegments(
    chunkDurations: number[],
    verses: DemoAudioVerse[]
  ): Array<{
    kind: 'verse' | 'heading';
    text: string;
    verse_number: number | null;
    start: number;
    end: number;
    chunk_index: number;
  }> {
    const verseTimings = this.buildMockVerseTimings(chunkDurations, verses);
    return verseTimings.map((timing, index) => ({
      kind: 'verse',
      text: verses[index]?.text ?? '',
      verse_number: timing.verse_number,
      start: timing.start,
      end: timing.end,
      chunk_index: timing.chunk_index,
    }));
  }

  //--------------------------------------------------------------------------
  // Route Handlers - Authentication
  //--------------------------------------------------------------------------

  //--------------------------------------------------------------------------
  // Route Handlers - Authentication
  //--------------------------------------------------------------------------

  private async handleLogin(req: HttpRequest<unknown>): Promise<MockResult> {
    await this.ensureUsersLoaded();
    const body = this.extractBody(req);
    const email = String(body['email'] || '').toLowerCase();
    const password = String(body['password'] || '');

    let user = this.users.find((entry) => entry.email.toLowerCase() === email);
    const shouldSeedUser =
      !user && this.environment.demoConfig?.login?.seedUserOnLogin && !!email;
    const seededUser = shouldSeedUser
      ? this.createDemoLoginUser(email, password)
      : null;

    const allowAnyCredentials =
      this.environment.demoConfig?.login?.allowAnyCredentials ?? false;
    const userToValidate = user ?? seededUser;
    const isPasswordValid =
      !!userToValidate &&
      (allowAnyCredentials || userToValidate.password === password);

    if (!isPasswordValid)
      return {
        status: 401,
        body: { success: false, message: 'Invalid credentials', data: null },
      };

    if (!user && seededUser) {
      this.users.push(seededUser);
      user = seededUser;
    }

    if (!user) {
      return {
        status: 401,
        body: { success: false, message: 'Invalid credentials', data: null },
      };
    }

    const profile = this.syncUserProfile(user);
    const tokens = this.issueToken(user.user_id);
    return {
      body: {
        success: true,
        message: 'Login successful',
        data: {
          user: profile,
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken,
        },
      },
    };
  }

  private createDemoLoginUser(email: string, password: string): DemoUser {
    const demoUser = this.environment.demoConfig?.demoUser;
    const allowAnyCredentials =
      this.environment.demoConfig?.login?.allowAnyCredentials ?? false;
    const fallbackPassword =
      this.environment.demoConfig?.login?.password || 'password123';
    let sequence = this.users.length + 1;
    let userId: string;
    do {
      userId = `demo-user-${sequence++}`;
    } while (this.users.some((entry) => entry.user_id === userId));

    return {
      user_id: userId,
      name: String(demoUser?.name || email.split('@')[0] || 'Demo User'),
      nickname: null,
      email,
      password: allowAnyCredentials && password ? password : fallbackPassword,
      role: 'user',
      avatar_fallback_url: '/assets/icon.png',
    };
  }

  private async handleRegister(req: HttpRequest<unknown>): Promise<MockResult> {
    await this.ensureUsersLoaded();
    const body = this.extractBody(req);
    const email = String(body['email'] || '').toLowerCase();
    if (this.users.some((entry) => entry.email.toLowerCase() === email)) {
      return {
        status: 409,
        body: {
          success: false,
          message: 'Email already registered',
          data: null,
        },
      };
    }

    const user: DemoUser = {
      user_id: `demo-user-${this.users.length + 1}`,
      name: String(body['name'] || 'New User'),
      email,
      password: String(body['password'] || 'password123'),
      role: 'user',
    };
    const profile = this.syncUserProfile(user);
    const tokens = this.issueToken(user.user_id);

    return {
      body: {
        success: true,
        message: 'Registration successful',
        data: {
          user: profile,
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken,
        },
      },
    };
  }

  private async handleLogout(req: HttpRequest<unknown>): Promise<MockResult> {
    const auth = req.headers.get('Authorization') || '';
    const token = auth.startsWith('Bearer ') ? auth.slice(7) : '';
    if (token) this.tokenToUserId.delete(token);
    return this.fromFile('/assets/mocks/auth/logout.json');
  }

  private async handleRefresh(req: HttpRequest<unknown>): Promise<MockResult> {
    const userId = this.getCurrentUserId(req);
    const tokens = this.issueToken(userId);
    const base = await this.loadJson<Record<string, unknown>>(
      '/assets/mocks/auth/refresh.json'
    );
    return {
      body: {
        ...base,
        data: {
          access_token: tokens.accessToken,
          refresh_token: tokens.refreshToken,
        },
      },
    };
  }

  private async handleCurrentUser(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    await this.ensureUsersLoaded();
    const userId = this.getCurrentUserId(req);
    const settings = this.getOrCreateSettingsPayload(userId);

    return {
      body: {
        success: true,
        message: 'Current user retrieved successfully',
        data: settings.user,
      },
    };
  }

  private async handleGetSettings(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    await this.ensureUsersLoaded();
    const userId = this.getCurrentUserId(req);
    const settings = this.getOrCreateSettingsPayload(userId);

    return {
      body: {
        success: true,
        message: 'Settings retrieved successfully',
        data: settings,
      },
    };
  }

  private async handleUpdateProfile(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    await this.ensureUsersLoaded();
    const userId = this.getCurrentUserId(req);
    const settings = this.getOrCreateSettingsPayload(userId);
    const body = this.extractBody(req);

    const nextUser = this.syncUserProfile({
      ...settings.user,
      name: String(body['name'] || settings.user.name),
      nickname:
        typeof body['nickname'] === 'string'
          ? String(body['nickname']).trim() || null
          : settings.user.nickname ?? null,
      email: String(body['email'] || settings.user.email),
      phone_number: body['phone_number']
        ? String(body['phone_number'])
        : null,
      bio: body['bio'] ? String(body['bio']) : null,
      updated_at: new Date().toISOString(),
    });

    settings.user = nextUser;

    return {
      body: {
        success: true,
        message: 'Profile updated successfully',
        data: nextUser,
      },
    };
  }

  private async handleUploadProfileAvatar(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    await this.ensureUsersLoaded();
    const userId = this.getCurrentUserId(req);
    const settings = this.getOrCreateSettingsPayload(userId);
    const requestBody = this.extractBody(req);
    const avatarFile = requestBody['avatar'];
    const uploadedAvatarUrl = this.createMockUploadedAvatarUrl(avatarFile);
    const avatarUrl =
      uploadedAvatarUrl ??
      settings.user.profile_image_url ??
      settings.user.avatar_url ??
      '/assets/user-avatar-man.jpg';

    const nextUser = this.syncUserProfile({
      ...settings.user,
      profile_image_url: avatarUrl ?? null,
      avatar_url: avatarUrl,
      profile_image: {
        url: avatarUrl ?? null,
        storage: avatarUrl ? 'demo-mock' : null,
        path: settings.user.profile_image?.path ?? avatarUrl,
        file_name: settings.user.profile_image?.file_name ?? 'demo-profile-image.jpg',
        mime_type: settings.user.profile_image?.mime_type ?? 'image/jpeg',
        size_bytes: settings.user.profile_image?.size_bytes ?? null,
        uploaded_at: new Date().toISOString(),
      },
      updated_at: new Date().toISOString(),
    });

    settings.user = nextUser;

    return {
      body: {
        success: true,
        message: 'Profile image updated successfully',
        data: nextUser,
      },
    };
  }

  private createMockUploadedAvatarUrl(avatarFile: unknown): string | null {
    if (!(avatarFile instanceof File)) {
      return null;
    }

    const encodedFileName = encodeURIComponent(avatarFile.name.trim() || 'profile-image');
    return `/assets/user-avatar-man.jpg?mock_upload=${Date.now()}&file_name=${encodedFileName}`;
  }

  //--------------------------------------------------------------------------
  // Route Handlers - Bible
  //--------------------------------------------------------------------------

  //--------------------------------------------------------------------------
  // Route Handlers - Bible
  //--------------------------------------------------------------------------

  private async handleVersions(): Promise<MockResult> {
    const data = await this.loadJson<Record<string, string>>(
      '/assets/mocks/bible/versions.json'
    );
    return { body: { success: true, message: '', data } };
  }

  private async handlePassage(req: HttpRequest<unknown>): Promise<MockResult> {
    const book = (this.getParam(req, 'book') || 'GEN').toUpperCase();
    const chapter = this.getParam(req, 'chapter') || '2';
    const version = (this.getParam(req, 'version') || 'NIV').toUpperCase();

    const payload = await this.loadJsonWithFallback<Record<string, unknown>>(
      `/assets/mocks/bible/passages/${book}.${chapter}.json`,
      '/assets/mocks/bible/passages/GEN.2.json'
    );
    return {
      body: {
        success: true,
        message: '',
        data: {
          ...payload,
          version,
          version_key: version,
          title: version,
          book,
          chapter,
        },
      },
    };
  }

  private async handleBibleBooks(
    _req: HttpRequest<unknown>
  ): Promise<MockResult> {
    const payload = await this.loadJsonWithFallback<Record<string, unknown>>(
      '/assets/mocks/bible/passages/GEN.2.json',
      '/assets/mocks/bible/passages/GEN.2.json'
    );

    return {
      body: {
        success: true,
        message: 'Books retrieved successfully',
        data: this.extractMockBibleBooks(payload).map((book) => ({
          code: book.usfm,
          name: book.human,
          abbreviation: book.abbreviation ?? '',
          chapters: book.chapters,
        })),
      },
    };
  }

  private async handleAudioVersions(): Promise<MockResult> {
    const seed = await this.loadAudioSeed();
    return {
      body: {
        success: true,
        message: 'Audio Bibles retrieved successfully',
        data: seed.versions,
      },
    };
  }

  private async handleAudioBooks(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    const seed = await this.loadAudioSeed();
    const version = (this.getParam(req, 'version') || 'KJV').toUpperCase();
    const books = seed.booksByVersion[version] ?? [];

    return {
      body: {
        success: true,
        message: 'Audio books retrieved successfully',
        data: {
          books,
          meta: {
            version,
            total_books: books.length,
          },
        },
      },
    };
  }

  private async handleAudioChapters(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    const seed = await this.loadAudioSeed();
    const version = (this.getParam(req, 'version') || 'KJV').toUpperCase();
    const book = (this.getParam(req, 'book') || 'GEN').toUpperCase();
    const chapters = seed.chaptersByVersionBook[version]?.[book] ?? [];

    return {
      body: {
        success: true,
        message: 'Audio chapters retrieved successfully',
        data: {
          chapters,
          meta: {
            version,
            book,
            total_chapters: chapters.length,
          },
        },
      },
    };
  }

  private async handleAudioChapter(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    const seed = await this.loadAudioSeed();
    const version = (this.getParam(req, 'version') || 'KJV').toUpperCase();
    const book = (this.getParam(req, 'book') || 'GEN').toUpperCase();
    const chapter = this.normalizePositiveInteger(
      this.getParam(req, 'chapter'),
      1
    );
    const requestedSpeakerId = this.normalizePositiveInteger(
      this.getParam(req, 'speaker_id'),
      0,
      0
    );
    const pageKey = `${version}:${book}:${chapter}`;
    const page = seed.pages[pageKey];

    if (!page) {
      return {
        status: 404,
        body: {
          success: false,
          message: 'Audio chapter not found',
          data: null,
        },
      };
    }

    const speakers = await Promise.all(
      page.speakers.map(async (speakerSeed) => {
        const profile =
          seed.speakerLibrary[String(speakerSeed.speaker_id)] ??
          ({} as DemoAudioSpeakerProfile);
        const chunkDurations = this.normalizeChunkDurations(
          speakerSeed.chunk_durations ?? []
        );
        const durationSeconds = Math.round(
          this.estimateMockChunkPlaybackDuration(chunkDurations)
        );
        const narrationSegments = this.buildMockNarrationSegments(
          chunkDurations,
          page.verses
        );

        return {
          speaker_id: speakerSeed.speaker_id,
          name: profile.name ?? `Speaker ${speakerSeed.speaker_id}`,
          details: profile.details ?? 'Narration',
          voice_type: profile.voice_type ?? 'Voice',
          is_premium: profile.is_premium ?? false,
          source: profile.source ?? null,
          language: profile.language ?? null,
          gender: profile.gender ?? null,
          country: profile.country ?? null,
          audio: {
            audio_url: speakerSeed.audio_url,
            download_url: speakerSeed.audio_url,
            format: 'mp3',
            duration_seconds: durationSeconds,
            size_bytes: 0,
            chunk_durations: chunkDurations,
            verse_timings: this.buildMockVerseTimings(
              chunkDurations,
              page.verses
            ),
            narration_segments: narrationSegments,
          },
        };
      })
    );

    const selectedSpeakerId =
      speakers.find((speaker) => speaker.speaker_id === requestedSpeakerId)
        ?.speaker_id ??
      speakers[0]?.speaker_id ??
      null;
    const activeAudio =
      speakers.find((speaker) => speaker.speaker_id === selectedSpeakerId)
        ?.audio ?? null;

    return {
      body: {
        success: true,
        message: 'Audio chapter retrieved successfully',
        data: {
          reference: page.reference,
          verses: page.verses,
          audio: activeAudio,
          speakers,
          selected_speaker_id: selectedSpeakerId,
          versions: seed.versions,
          books: seed.booksByVersion[version] ?? [],
          chapters: seed.chaptersByVersionBook[version]?.[book] ?? [],
          bible_books: seed.bibleBooksByVersion[version] ?? [],
          has_audio: !!activeAudio?.audio_url,
        },
      },
    };
  }

  private handleAudioPreferences(req: HttpRequest<unknown>): MockResult {
    const userId = this.getCurrentUserId(req);
    const preferences = this.audioUserPreferences.get(userId) ?? {};

    return {
      body: {
        success: true,
        message: 'Audio preferences retrieved successfully',
        data: preferences,
      },
    };
  }

  private handleSaveAudioPreferences(req: HttpRequest<unknown>): MockResult {
    const userId = this.getCurrentUserId(req);
    const currentPreferences = this.audioUserPreferences.get(userId) ?? {};
    const nextPreferences = {
      ...currentPreferences,
      ...this.extractBody(req),
    };

    this.audioUserPreferences.set(userId, nextPreferences);

    return {
      body: {
        success: true,
        message: 'Audio preferences saved successfully',
        data: nextPreferences,
      },
    };
  }

  private handleAudioTrackPlayback(req: HttpRequest<unknown>): MockResult {
    const body = this.extractBody(req);
    const userId = this.getCurrentUserId(req);
    const version = String(body['version'] || 'KJV').toUpperCase();
    const book = String(body['book'] || 'GEN').toUpperCase();
    const chapter = this.normalizePositiveInteger(body['chapter'], 1);
    const speakerId = this.normalizePositiveInteger(body['speaker_id'], 0, 0);
    const key = [userId, version, book, chapter, speakerId].join(':');

    this.audioPlaybackProgress.set(key, {
      progress_seconds: this.normalizeNonNegativeNumber(
        body['progress_seconds']
      ),
      duration_seconds: this.normalizeNonNegativeNumber(
        body['duration_seconds']
      ),
      completed:
        body['completed'] === true ||
        String(body['completed']).toLowerCase() === 'true' ||
        Number(body['completed']) === 1,
      updated_at: new Date().toISOString(),
    });

    return {
      body: {
        success: true,
        message: 'Playback tracked successfully',
        data: null,
      },
    };
  }

  private extractMockBibleBooks(payload: Record<string, unknown>): Array<{
    usfm: string;
    human: string;
    abbreviation?: string;
    chapters: Array<{
      usfm: string;
      human: string;
      canonical?: boolean;
      toc?: boolean;
    }>;
  }> {
    const rawBibleData = payload['bibleData'];
    let bibleData: { books?: Array<Record<string, unknown>> } | null = null;

    if (typeof rawBibleData === 'string') {
      try {
        bibleData = JSON.parse(rawBibleData) as {
          books?: Array<Record<string, unknown>>;
        };
      } catch {
        bibleData = null;
      }
    } else if (rawBibleData && typeof rawBibleData === 'object') {
      bibleData = rawBibleData as { books?: Array<Record<string, unknown>> };
    }

    if (!Array.isArray(bibleData?.books)) {
      return [];
    }

    return bibleData.books
      .filter(
        (
          book
        ): book is Record<string, unknown> & {
          usfm: string;
          human?: string;
          abbreviation?: string;
          chapters: Array<Record<string, unknown>>;
        } => typeof book['usfm'] === 'string' && Array.isArray(book['chapters'])
      )
      .map((book) => ({
        usfm: book.usfm,
        human:
          typeof book.human === 'string' && book.human.trim() !== ''
            ? book.human
            : book.usfm,
        abbreviation:
          typeof book.abbreviation === 'string' ? book.abbreviation : undefined,
        chapters: book.chapters.map((chapter) => ({
          usfm: String(chapter['usfm'] ?? ''),
          human: String(chapter['human'] ?? ''),
          canonical:
            typeof chapter['canonical'] === 'boolean'
              ? chapter['canonical']
              : undefined,
          toc: typeof chapter['toc'] === 'boolean' ? chapter['toc'] : undefined,
        })),
      }));
  }

  //--------------------------------------------------------------------------
  // Route Handlers - Themes
  //--------------------------------------------------------------------------

  private async handlePassageThemes(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    const book = this.getParam(req, 'book').toUpperCase();
    const chapter = this.getParam(req, 'chapter');
    const chapterUsfm = `${book}.${chapter}`;
    const themes = await this.loadJson<Array<Record<string, unknown>>>(
      '/assets/mocks/themes/passage-themes.json'
    );

    const data = themes
      .filter((item) => String(item['chapter_id']) === chapterUsfm)
      .map((item) => ({
        theme: Number(item['theme'] || 0),
        occurrence_count: Number(item['occurrence_count'] || 0),
        theme_name: String(item['theme_name'] || ''),
      }))
      .sort((a, b) => b.occurrence_count - a.occurrence_count);

    return { body: { success: true, message: '', data } };
  }

  private async handleThemeChapters(
    req: HttpRequest<unknown>,
    path: string
  ): Promise<MockResult> {
    const rawThemeId = path.match(/^\/themes\/(\d+)\/chapters$/)?.[1];
    const themeId = rawThemeId ? Number(rawThemeId) : -1;
    if (themeId < 0) {
      return {
        status: 400,
        body: {
          success: false,
          message: 'Invalid theme id',
          theme_name: '',
          data: [],
        },
      };
    }

    const limit = Number(this.getParam(req, 'limit') || 50);
    const page = Number(this.getParam(req, 'page') || 1);
    const book = this.getParam(req, 'book').toUpperCase();

    const allEntries = await this.loadJson<Array<Record<string, unknown>>>(
      '/assets/mocks/themes/passage-themes.json'
    );

    // Derive theme name from the first matching entry
    const firstMatch = allEntries.find(
      (item) => Number(item['theme'] || 0) === themeId
    );
    const themeName = firstMatch ? String(firstMatch['theme_name'] || '') : '';

    const filtered = allEntries
      .filter((item) => Number(item['theme'] || 0) === themeId)
      .filter((item) =>
        book ? String(item['chapter_id'] || '').startsWith(`${book}.`) : true
      )
      .map((item) => ({
        chapter_usfm: String(item['chapter_id'] || ''),
        occurrence_count: Number(item['occurrence_count'] || 0),
      }))
      .sort((a, b) => b.occurrence_count - a.occurrence_count);

    const safeLimit = Math.max(1, limit);
    const safePage = Math.max(1, page);
    const offset = (safePage - 1) * safeLimit;
    if (offset >= filtered.length) {
      return {
        body: { success: true, message: '', theme_name: themeName, data: [] },
      };
    }

    return {
      body: {
        success: true,
        message: '',
        theme_name: themeName,
        data: filtered.slice(offset, offset + safeLimit),
      },
    };
  }

  private async handleDictionaryHome(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    return this.dictionaryState.handleDictionaryHome({
      tab: this.getParam(req, 'tab') || 'popular',
      searchQuery: this.getParam(req, 'q') || '',
      dictionaryId: this.getParam(req, 'dictionary_id') || '',
      letter: this.getParam(req, 'letter') || 'A',
      searchScope: this.getParam(req, 'search_scope') || 'topic',
      matchMode: this.getParam(req, 'match_mode') || 'contains',
      strongOnly: this.getParam(req, 'strong_only') === '1',
      atlasOnly: this.getParam(req, 'atlas_only') === '1',
      wordLimit: Number(this.getParam(req, 'word_limit') || 24),
      wordOffset: Number(this.getParam(req, 'word_offset') || 0),
    });
  }

  private async handleDictionaryEntry(
    req: HttpRequest<unknown>
  ): Promise<MockResult> {
    return this.dictionaryState.handleDictionaryEntry({
      dictionaryId: this.getParam(req, 'dictionary_id') || '',
      topic: this.getParam(req, 'topic') || 'Aaron',
      wordLetter: this.getParam(req, 'word_letter') || '',
      view: this.getParam(req, 'view') === 'all' ? 'all' : 'single',
      wordLimit: Number(this.getParam(req, 'word_limit') || 24),
      wordOffset: Number(this.getParam(req, 'word_offset') || 0),
    });
  }

  private handleDictionaryResume(req: HttpRequest<unknown>): MockResult {
    return this.dictionaryState.handleResume({
      userId: this.getCurrentUserId(req),
      resourceType: this.getParam(req, 'resource_type') || 'dictionary',
      resourceId: this.getParam(req, 'resource_id') || '',
      entryKey: this.getParam(req, 'entry_key') || '',
    });
  }

  private handleStartStudyTracking(req: HttpRequest<unknown>): MockResult {
    return this.dictionaryState.startSession(
      this.getCurrentUserId(req),
      this.extractBody(req)
    );
  }

  private handleUpdateStudyTracking(
    req: HttpRequest<unknown>,
    path: string
  ): MockResult {
    const sessionKey = path.split('/').pop() || '';
    return this.dictionaryState.updateSession(
      sessionKey,
      this.extractBody(req)
    );
  }

  private handleEndStudyTracking(
    req: HttpRequest<unknown>,
    path: string
  ): MockResult {
    const sessionKey = path.match(/^\/study-tracking\/(.+)\/end$/)?.[1] || '';
    return this.dictionaryState.endSession(sessionKey, this.extractBody(req));
  }
  //--------------------------------------------------------------------------
  // Route Handlers - Highlights
  //--------------------------------------------------------------------------

  private handleSaveHighlight(req: HttpRequest<unknown>): MockResult {
    const body = this.extractBody(req);
    const verseId = String(body['verseId'] || '').toUpperCase();
    const color = String(body['color'] || 'yellow').toLowerCase();
    const version = String(body['version'] || 'NIV').toUpperCase();
    const userId = this.getCurrentUserId(req);
    const chapterUsfm = verseId.split('.').slice(0, 2).join('.');

    // Remove existing highlight for this verse
    this.highlights = this.highlights.filter(
      (item) =>
        !(
          item.verse_id === verseId &&
          item.user_id === userId &&
          item.version === version
        )
    );

    // Add new highlight if color is not 'none'
    if (color !== 'none') {
      this.highlights.push({
        id: `hl-${Date.now()}`,
        verse_id: verseId,
        chapter_usfm: chapterUsfm,
        color,
        version,
        user_id: userId,
      });
    }

    return { body: { success: true, message: 'Highlight saved', data: null } };
  }

  //--------------------------------------------------------------------------
  // Route Handlers - Notes
  //--------------------------------------------------------------------------

  //--------------------------------------------------------------------------
  // Route Handlers - Notes
  //--------------------------------------------------------------------------

  private handleSaveNote(req: HttpRequest<unknown>): MockResult {
    const body = this.extractBody(req);
    const verseId = String(body['verseId'] || '').toUpperCase();
    const version = String(body['version'] || 'NIV').toUpperCase();
    const userId = this.getCurrentUserId(req);
    const chapterUsfm = verseId.split('.').slice(0, 2).join('.');

    const note: DemoNote = {
      id: `note-${Date.now()}`,
      verse_id: verseId,
      chapter_usfm: chapterUsfm,
      type: (body['type'] as 'text' | 'audio') || 'text',
      content: (body['content'] as string) || null,
      audio_url: (body['audioUrl'] as string) || null,
      duration: body['duration'] ? String(body['duration']) : null,
      version,
      user_id: userId,
    };

    this.notes.push(note);
    return { body: { success: true, message: 'Note saved', data: null } };
  }

  private handleDeleteNote(
    req: HttpRequest<unknown>,
    path: string
  ): MockResult {
    const verseId = path.replace('/notes/', '').toUpperCase();
    const prevLength = this.notes.length;
    this.notes = this.notes.filter(
      (item) => item.id.toUpperCase() !== verseId && item.verse_id !== verseId
    );

    if (prevLength === this.notes.length) {
      return {
        status: 404,
        body: { success: false, message: 'Note not found', data: null },
      };
    }

    return { body: { success: true, message: 'Note deleted', data: null } };
  }

  //--------------------------------------------------------------------------
  // Route Handlers - Reading Sessions
  //--------------------------------------------------------------------------

  private handleStartReadingSession(req: HttpRequest<unknown>): MockResult {
    const body = this.extractBody(req);
    const userId = this.getCurrentUserId(req);
    const logId = `session-${Date.now()}`;

    this.readingSessions.push({
      id: logId,
      user_id: userId,
      book: String(body['book'] || '').toUpperCase(),
      chapter: String(body['chapter'] || ''),
      version: String(body['version'] || '').toUpperCase(),
      duration: 0,
      status: 'active',
    });

    return {
      body: {
        success: true,
        message: 'Activity started',
        data: { log_id: logId, joined_existing: false, current_duration: 0 },
      },
    };
  }

  private handleUpdateReadingSession(
    req: HttpRequest<unknown>,
    path: string
  ): MockResult {
    const logId = path.split('/').pop() || '';
    const body = this.extractBody(req);
    const duration = Number(body['duration_seconds'] ?? body['duration'] ?? 0);
    const session = this.readingSessions.find((entry) => entry.id === logId);

    if (!session) {
      return {
        status: 404,
        body: {
          success: false,
          message: 'Reading session not found',
          data: null,
        },
      };
    }

    session.duration = Math.max(session.duration, duration);
    return {
      body: {
        success: true,
        message: 'Activity updated',
        data: { current_duration: session.duration },
      },
    };
  }

  private handleEndReadingSession(
    req: HttpRequest<unknown>,
    path: string
  ): MockResult {
    const logId =
      path.match(/^\/user\/reading-sessions\/(.+)\/end$/)?.[1] || '';
    const body = this.extractBody(req);
    const duration = Number(body['duration_seconds'] ?? body['duration'] ?? 0);
    const session = this.readingSessions.find((entry) => entry.id === logId);

    if (!session) {
      return {
        status: 404,
        body: {
          success: false,
          message: 'Reading session not found',
          data: null,
        },
      };
    }

    session.duration = Math.max(session.duration, duration);
    session.status = 'ended';
    return {
      body: {
        success: true,
        message: 'Activity ended',
        data: { final_duration: session.duration },
      },
    };
  }
}
